import moment from "moment";
import CoreAPI from "./CoreAPI";
import { transformCamelToSnake } from "./utils/transformCamelToSnake";
import { transformAPIKeyToCamel } from "./utils/transformAPIKeyToCamel";

const apiClient = new CoreAPI();

export interface LogisticSettingsParams {
  deployCvsSuspendDisplayName?: string;
  deployCvsSuspendStartDate?: string;
  deployCvsSuspendEndDate?: string;
  tcatProcessingDays?: number;
  tcatDeliveringDays?: number;
  storeProcessingDays?: number;
  storeDeliveringDays?: number;
}

export interface PreviewEstimatedArrivalParams {
  body: LogisticSettingsParams;
  isHome: boolean;
  estOrderDate: string;
}

interface ConfigSettingApi {
  fetchEnvironmentList: () => Promise<EnvironmentParams>;
  fetchUpdateEnvironmentList: (params: EnvironmentParams) => Promise<EnvironmentParams>;
  fetchPurgeLoginCache: () => Promise<void>;
  fetchLogisticSettings: () => Promise<LogisticSettingsParams>;
  postLogisticSettings: (params: LogisticSettingsParams) => Promise<LogisticSettingsParams>;
  postLogisticSettingsPreview: (params: PreviewEstimatedArrivalParams) => Promise<string>;
}

export interface EnvironmentParams {
  defaultRecommendPageIds?: number[];
  vipPromotionStartDate?: string;
  vipPromotionContent?: string;
  defaultAdditionPurchaseApplyMaxQty?: string;
  promotionMaxNumberOfBindRules?: string;
  vipPromotionImage?: string;
  cvsLimitLongestEdge?: string;
  cvsLimitSecondLongestEdge?: string;
  cvsLimitShortestEdge?: string;
  cvsLimitVolume?: string;
  cvsLimitWeight?: string;
  cvsLimitPrice?: string;
  newCustomerPromotionRuleIds?: number[];
  vipCustomerRenewPromotionRuleIds?: number[];
  searchKeywords?: string[];
  vipPromotionEndDate?: string;
  deployCvsSuspendStartDate?: string;
  deployCvsSuspendEndDate?: string;
  serverFields?: [];
}
export interface FormatedEnvironmentResult {
  timePeriod: moment.Moment[];
  defaultRecommendPageIds: number[];
  vipPromotionStartDate: string;
  vipPromotionContent: string;
  defaultAdditionPurchaseApplyMaxQty: string;
  promotionMaxNumberOfBindRules: string;
  vipPromotionImage: string;
  cvsLimitLongestEdge: string;
  cvsLimitSecondLongestEdge: string;
  cvsLimitShortestEdge: string;
  cvsLimitVolume: string;
  cvsLimitWeight: string;
  cvsLimitPrice: string;
  newCustomerPromotionRuleIds: number[];
  searchKeywords: string[];
  vipPromotionEndDate: string;
  deployCvsSuspendStartDate: string;
  deployCvsSuspendEndDate: string;
}

const configSettingApi: ConfigSettingApi = {
  fetchEnvironmentList: async () => {
    const getParams = {};
    const response = await apiClient.getData("/manage/global-preferences/", getParams);
    return transformAPIKeyToCamel(response.data.result);
  },
  fetchUpdateEnvironmentList: async (param) => {
    const requestParam = transformCamelToSnake(param);
    const response = await apiClient.patchData("/manage/global-preferences/batch-patch/", requestParam);
    return transformAPIKeyToCamel(response.data.result);
  },
  fetchPurgeLoginCache: async () => {
    await apiClient.getData("/manage/throttle/purge/", {});
  },
  fetchLogisticSettings: async () => {
    const response = await apiClient.getData("/manage/logistic/settings/", {});
    return transformAPIKeyToCamel(response.data.result) as LogisticSettingsParams;
  },
  postLogisticSettings: async (params: LogisticSettingsParams) => {
    const requestParam = transformCamelToSnake(params);
    const response = await apiClient.postData("/manage/logistic/settings/", requestParam);
    return transformAPIKeyToCamel(response.data.result) as LogisticSettingsParams;
  },
  postLogisticSettingsPreview: async (params: PreviewEstimatedArrivalParams) => {
    const queryString = new URLSearchParams({
      is_home: String(params.isHome),
      est_order_date: params.estOrderDate,
    }).toString();
    const body = transformCamelToSnake(params.body);
    const response = await apiClient.postData(`/manage/logistic/settings/preview/?${queryString}`, body);
    return response.data.result ?? "";
  },
};

export default configSettingApi;
